// Browser client for the OpenClaw Gateway WebSocket protocol (v4).
// Speaks directly to the pod's openclaw-gateway over WSS, using a
// shared-secret token minted by the hub backend at /api/openclaw/gateway/config.
//
// Frame shapes (per https://docs.openclaw.ai/gateway/protocol):
//   req:   { type: 'req',   id, method, params }
//   res:   { type: 'res',   id, ok, payload | error }
//   event: { type: 'event', event, payload, seq?, stateVersion? }
//
// Connection lifecycle:
//   1. Open WSS, expect first frame: event connect.challenge {nonce, ts}
//   2. Send req connect {minProtocol, maxProtocol, client, role, scopes,
//      auth:{token}, device:{nonce}}
//   3. On res ok hello-ok: capture policy.tickIntervalMs / maxPayload
//   4. RPC: chat.send, agent, tools.invoke, tools.catalog
//   5. Streaming results arrive as chat events with deltaText (v4)
//
// This client does NOT implement device.signature (ed25519). That requires
// the pod gateway to be in token-only auth mode (the default for hub.moltpodapps
// pods today). If signature is required by the gateway, the connect will fail
// and the panel will surface error.details.recommendedNextStep.

// Gateway accepts protocol v3 or v4. v4 adds chat.deltaText; v3 returns full
// snapshots only. We accept either.
const PROTOCOL_MIN = 3;
const PROTOCOL_MAX = 4;
const DEFAULT_TICK_INTERVAL_MS = 30_000;
const RECONNECT_INITIAL_MS = 2_000;
const RECONNECT_MAX_MS = 30_000;
const RECONNECT_MAX_ATTEMPTS = 5;

let idCounter = 0;
function nextId(prefix = 'req') {
  idCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${idCounter}`;
}

// Pull plain text out of a gateway chat message envelope.
// chat events carry payload.message.content: [{type:'text', text:'...'}].
function extractAssistantText(message) {
  if (!message) return '';
  if (typeof message.text === 'string') return message.text;
  const content = Array.isArray(message.content) ? message.content : null;
  if (!content) return '';
  return content
    .map((part) => (part && typeof part.text === 'string' ? part.text : ''))
    .join('');
}

// Schema enumerates valid client.id literals. `webchat-ui` and
// `openclaw-control-ui` trigger a Control-UI origin allowlist + device-identity
// requirement. `gateway-client` accepts a plain shared-token connect with no
// device signing and no origin allowlist, so we use that for the browser FAB.
function clientMeta() {
  let platform = 'web';
  try { platform = (navigator.userAgentData?.platform) || navigator.platform || 'web'; } catch {}
  return {
    id: 'gateway-client',
    displayName: 'Hub Gateway FAB',
    version: '1',
    platform,
    mode: 'ui',
  };
}

export class GatewayClient extends EventTarget {
  constructor({ adoptDefaultConversation = true } = {}) {
    super();
    // Per-window clients pass false: they are dedicated pipes driven by an
    // explicit session key and must not adopt (or lazily create) the user's
    // default hub conversation on connect.
    this.adoptDefaultConversation = adoptDefaultConversation;
    this.ws = null;
    this.state = 'idle'; // idle | connecting | handshaking | ready | closed | error | stopped
    this.reconnectDelayMs = RECONNECT_INITIAL_MS;
    this.reconnectAttempts = 0;
    this.reconnectTimer = null;
    this.shouldReconnect = true;
    this.pending = new Map(); // id -> { resolve, reject, onEvent }
    this.streams = new Map(); // messageId -> stream state for chat events
    this.policy = { tickIntervalMs: DEFAULT_TICK_INTERVAL_MS, maxPayload: 1_000_000 };
    this.lastTickAt = 0;
    this.tickWatchdog = null;
    this.config = null; // { ws_url, token, scopes }
    this.connectController = null;
  }

  _setState(next, detail = {}) {
    if (this.state === next) return;
    this.state = next;
    this.dispatchEvent(new CustomEvent('state', { detail: { state: next, ...detail } }));
  }

  async connect({ _isAutoRetry = false } = {}) {
    if (this.state === 'connecting' || this.state === 'handshaking' || this.state === 'ready') return;
    if (!_isAutoRetry) {
      // Caller-initiated (panel open / retry button): a retry is already on
      // the clock — let it run rather than resetting the budget, otherwise
      // render-loop callers can turn a dead gateway into an unbounded
      // connect storm.
      if (this.reconnectTimer) return;
      this.reconnectAttempts = 0;
      this.reconnectDelayMs = RECONNECT_INITIAL_MS;
    }
    this.shouldReconnect = true;
    this._setState('connecting');
    try {
      const r = await fetch('/api/openclaw/gateway/config', { credentials: 'same-origin' });
      if (!r.ok) {
        const body = await r.text().catch(() => '');
        throw new Error(`gateway config ${r.status} ${body.slice(0, 200)}`);
      }
      this.config = await r.json();
    } catch (err) {
      this._setState('error', { error: String(err.message || err) });
      this._scheduleReconnect();
      return;
    }
    this._openSocket();
  }

  _openSocket() {
    let ws;
    try {
      ws = new WebSocket(this.config.ws_url);
    } catch (err) {
      this._setState('error', { error: String(err.message || err) });
      this._scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this._setState('handshaking');
    };
    ws.onmessage = (ev) => {
      let frame;
      try { frame = JSON.parse(ev.data); } catch { return; }
      this._onFrame(frame);
    };
    ws.onclose = (ev) => {
      this._teardown();
      this._setState('closed', { code: ev.code, reason: String(ev.reason || '') });
      if (this.shouldReconnect) this._scheduleReconnect();
    };
    ws.onerror = () => {
      // onclose will fire after this — leave reconnect to it.
    };
  }

  _teardown() {
    if (this.tickWatchdog) {
      clearInterval(this.tickWatchdog);
      this.tickWatchdog = null;
    }
    for (const [, { reject }] of this.pending) {
      try { reject(new Error('connection closed')); } catch {}
    }
    this.pending.clear();
    this.streams.clear();
    this.lastTickAt = 0;
  }

  _scheduleReconnect(reason = '') {
    if (!this.shouldReconnect) return;
    // At most one pending retry per client — repeated failure events (or
    // callers re-invoking connect) must not stack parallel retry chains.
    if (this.reconnectTimer) return;
    if (this.reconnectAttempts >= RECONNECT_MAX_ATTEMPTS) {
      this.shouldReconnect = false;
      this._setState('stopped', { error: `gave up after ${this.reconnectAttempts} attempts${reason ? ` (${reason})` : ''}` });
      return;
    }
    this.reconnectAttempts += 1;
    const delay = this.reconnectDelayMs;
    this.reconnectDelayMs = Math.min(this.reconnectDelayMs * 2, RECONNECT_MAX_MS);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.shouldReconnect) return;
      this.connect({ _isAutoRetry: true });
    }, delay);
  }

  close() {
    this.shouldReconnect = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try { this.ws.close(1000, 'client close'); } catch {}
    }
    this._teardown();
    this._setState('idle');
  }

  _onFrame(frame) {
    // Optional debug logging: localStorage.setItem('hub-gw2-trace', '1') to
    // see every frame as it arrives. Useful for chasing missing tool
    // events / verifying caps negotiation.
    try {
      if (typeof localStorage !== 'undefined' && localStorage.getItem('hub-gw2-trace') === '1') {
        if (frame.type === 'event') {
          const p = frame.payload || {};
          const isTool = p.stream === 'tool' || frame.event === 'session.tool';
          if (isTool) {
            const d = p.data || {};
            console.debug('[gw-trace] TOOL', {
              event: frame.event,
              phase: d.phase,
              name: d.name,
              toolCallId: d.toolCallId,
              argsType: typeof d.args,
              argsKeys: d.args && typeof d.args === 'object' ? Object.keys(d.args) : null,
              argsPreview: typeof d.args === 'string' ? d.args.slice(0, 120) : null,
              hasResult: d.result !== undefined,
              resultType: typeof d.result,
              isError: d.isError,
              data: d,
            });
          } else {
            const compact = { event: frame.event, stream: p.stream, state: p.state, phase: p.data?.phase || p.phase, name: p.data?.name || p.name || p.tool?.name, runId: p.runId, seq: p.seq };
            console.debug('[gw-trace] event', compact);
          }
        } else if (frame.type === 'res') {
          console.debug('[gw-trace] res', frame.id, frame.ok ? 'ok' : `err: ${frame.error?.message}`);
        }
      }
    } catch {}

    if (frame.type === 'event') {
      this._onEvent(frame);
      return;
    }
    if (frame.type === 'res') {
      const entry = this.pending.get(frame.id);
      if (!entry) return;
      this.pending.delete(frame.id);
      if (frame.ok) entry.resolve(frame.payload);
      else entry.reject(Object.assign(new Error(frame.error?.message || 'gateway error'), { error: frame.error }));
      return;
    }
  }

  _onEvent(frame) {
    if (frame.event === 'connect.challenge') {
      const nonce = frame.payload?.nonce;
      this._sendConnect(nonce);
      return;
    }
    if (frame.event === 'tick' || frame.event === 'heartbeat') {
      this.lastTickAt = Date.now();
      return;
    }
    // ── Session attribution ────────────────────────────────────────────
    // This is ONE shared socket for every chat window (and it receives the
    // broadcast frames of every agent:main run, including backend jobs like
    // ICP fit). So we must tell each downstream listener which chat session a
    // frame belongs to. Streaming deltas often carry only a runId, so we keep
    // a run→session map (learned from any frame that carries a sessionKey —
    // session.message always does) to resolve them. Every dispatched 'stream'
    // detail gets a `sessionKey`, and every 'event' frame gets `__sk`; each
    // window's adapter drops anything belonging to a *different* session.
    if (!this._runSession) this._runSession = new Map();
    const _sk = frame.payload?.sessionKey || null;
    const _rid = frame.payload?.runId;
    const _mid = frame.payload?.message?.id;
    if (_sk) {
      if (_rid != null) this._runSession.set(_rid, _sk);
      if (_mid != null) this._runSession.set(_mid, _sk);
      if (this._runSession.size > 600) this._runSession.delete(this._runSession.keys().next().value);
    }
    const skFor = (rid) => _sk || (rid != null ? (this._runSession.get(rid) || null) : null);
    frame.__sk = skFor(_rid);

    // The gateway streams assistant text via `agent` events with
    // payload.stream === 'assistant' (cumulative `data.text` per chunk).
    // It marks completion via `chat` events with payload.state === 'final'.
    // Lifecycle and tool events become progress signals for the panel.
    if (frame.event === 'agent') {
      const p = frame.payload || {};
      if (p.stream === 'assistant' && p.data && typeof p.data.text === 'string') {
        this._dispatchAssistantStream(p.runId, p.data.text, /* done */ false, skFor(p.runId));
        return;
      }
      // lifecycle and other agent sub-streams go to the generic event
      // pipeline so the panel can render them as progress labels.
      this.dispatchEvent(new CustomEvent('event', { detail: frame }));
      return;
    }
    if (frame.event === 'chat') {
      const p = frame.payload || {};
      const text = extractAssistantText(p?.message);
      if (p.state === 'final') {
        this._dispatchAssistantStream(p.runId, text, /* done */ true, skFor(p.runId));
      } else if (p.state === 'delta' && text) {
        this._dispatchAssistantStream(p.runId, text, /* done */ false, skFor(p.runId));
      }
      return;
    }
    if (frame.event === 'session.message') {
      // session.message carries a FULL message snapshot for any role
      // (assistant / user / toolResult / system). We need to surface
      // these to the adapter so it can build tool-call parts AND, for
      // assistant messages only, also dispatch the text through the
      // stream channel so the bubble's text part fills in. Treating a
      // USER message's text as an assistant stream delta echoes the
      // operator's own wrapped envelope back into the assistant bubble
      // — that's how the 'System: [time] Exec completed…' preamble
      // ended up rendering inside a cream bubble.
      const p = frame.payload || {};
      const role = p?.message?.role;
      if (role === 'assistant') {
        const text = extractAssistantText(p?.message);
        if (text) this._dispatchAssistantStream(p.runId || p.message?.id || 'msg', text, !!p.done, p.sessionKey || skFor(p.runId));
      }
      // intentional fall-through to dispatchEvent('event', ...) below
    }
    // Surface everything else so the panel can inspect operations / tool calls.
    this.dispatchEvent(new CustomEvent('event', { detail: frame }));
  }

  _dispatchAssistantStream(messageId, text, done, sessionKey = null) {
    if (!messageId) return;
    let stream = this.streams.get(messageId);
    if (!stream) {
      stream = { messageId, text: '', role: 'assistant', done: false };
      this.streams.set(messageId, stream);
    }
    if (typeof text === 'string' && text.length >= stream.text.length) stream.text = text;
    else if (typeof text === 'string' && text) stream.text = text;
    if (done) stream.done = true;
    this.dispatchEvent(new CustomEvent('stream', {
      detail: { messageId, text: stream.text, done: stream.done, role: 'assistant', sessionKey },
    }));
    if (stream.done) this.streams.delete(messageId);
  }

  _sendConnect(nonce) {
    const id = nextId('connect');
    // `device` is optional in the connect schema. We don't sign in the browser
    // (no ed25519 identity), so we omit it entirely — the gateway is in
    // shared-token auth mode and accepts token-only.
    void nonce;
    this._send({
      type: 'req',
      id,
      method: 'connect',
      params: {
        minProtocol: PROTOCOL_MIN,
        maxProtocol: PROTOCOL_MAX,
        client: clientMeta(),
        role: 'operator',
        scopes: this.config?.scopes || ['operator.read', 'operator.write', 'operator.approvals'],
        // Opt into the gateway's per-run tool event stream. Without this
        // cap declared the server filters our connId out of the tool
        // broadcast recipient set, so the FAB never sees live tool
        // calls (only the post-run chat.history canonicalisation).
        caps: ['tool-events'],
        // Hub proxy rewrites auth + device with the pod's signed identity.
        // We still send a stub so any version that doesn't proxy can be told
        // the browser is not the auth boundary.
        auth: {},
      },
    }).then(async (payload) => {
      if (payload?.policy?.tickIntervalMs) this.policy.tickIntervalMs = payload.policy.tickIntervalMs;
      if (payload?.policy?.maxPayload) this.policy.maxPayload = payload.policy.maxPayload;
      this.mainSessionKey = payload?.snapshot?.sessionDefaults?.mainSessionKey || 'agent:main:main';
      this.sessionKey = this.mainSessionKey;
      // Adopt the user's hub-tracked conversation as the default lane so FAB
      // chats don't share (and pollute) the pod agent's main working context.
      // The hub allocates durable keys (agent:main:hub-<user>-<n>) and the
      // gateway creates the session lazily on first chat.send. Best-effort
      // with a short timeout: on any failure we stay on mainSessionKey.
      if (this.adoptDefaultConversation) {
        try {
          const ctl = new AbortController();
          const timer = setTimeout(() => ctl.abort(), 2500);
          const r = await fetch('/api/openclaw/chat-sessions/default', {
            method: 'POST',
            credentials: 'same-origin',
            signal: ctl.signal,
          });
          clearTimeout(timer);
          if (r.ok) {
            const conv = await r.json();
            if (conv && typeof conv.key === 'string' && conv.key) {
              this.sessionKey = conv.key;
              this.conversation = conv;
            }
          }
        } catch { /* hub API unavailable — stay on the gateway main session */ }
      }
      this.reconnectDelayMs = RECONNECT_INITIAL_MS;
      this.lastTickAt = Date.now();
      this._startTickWatchdog();
      // Subscribe to per-session events so the runtime fans
      // `session.tool` / `session.message` frames to our connId. Tool
      // events the gateway emits during an agent run reach us this way
      // even if the `caps:['tool-events']` recipient registration fell
      // through. Best-effort: a rejection just leaves us on the
      // narrower assistant-text stream.
      try {
        await this._send({
          type: 'req',
          id: nextId('sessions-subscribe'),
          method: 'sessions.subscribe',
          params: {},
        });
      } catch (subErr) {
        console.warn('[gateway] sessions.subscribe failed', subErr?.message || subErr);
      }
      // We used to call `sessions.patch verboseLevel='full'` here to make
      // the gateway include `result` / `partialResult` in mid-run tool
      // event broadcasts. That call requires operator.admin scope, which
      // this connection doesn't carry, so it always failed with
      // INVALID_REQUEST / "missing scope: operator.admin" and surfaced
      // as a console error every connect. Drop it — the chat.history
      // polling in the adapter already fills in tool results post-run,
      // and live tool args still render via the per-tool stream we
      // opted into via caps:['tool-events'].
      this._setState('ready', {
        protocol: payload?.protocol,
        scopes: payload?.auth?.scopes,
        sessionKey: this.sessionKey,
      });
    }).catch((err) => {
      const details = err.error || null;
      const code = details?.code || '';
      // Non-retryable: schema rejection, bad token, scope rejection.
      const fatal = !details?.retryable && /INVALID_REQUEST|AUTH|UNAUTHORIZED|FORBIDDEN/i.test(code);
      this._setState('error', { error: String(err.message || err), details });
      this.shouldReconnect = !fatal;
      try { this.ws?.close(4001, 'connect failed'); } catch {}
    });
  }

  _startTickWatchdog() {
    if (this.tickWatchdog) clearInterval(this.tickWatchdog);
    const interval = Math.max(5_000, Math.floor(this.policy.tickIntervalMs / 2));
    this.tickWatchdog = setInterval(() => {
      if (!this.lastTickAt) return;
      const elapsed = Date.now() - this.lastTickAt;
      if (elapsed > this.policy.tickIntervalMs * 2) {
        try { this.ws?.close(4000, 'tick timeout'); } catch {}
      }
    }, interval);
  }

  _send(frame) {
    return new Promise((resolve, reject) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        reject(new Error('socket not open'));
        return;
      }
      if (frame.type === 'req') this.pending.set(frame.id, { resolve, reject });
      try {
        this.ws.send(JSON.stringify(frame));
        if (frame.type !== 'req') resolve();
      } catch (err) {
        this.pending.delete(frame.id);
        reject(err);
      }
    });
  }

  // ---- public RPC surface ----

  // Allocate a fresh hub-tracked conversation and switch this connection to
  // it. Unlike the old client-side random key rotation, the hub records the
  // key, so the conversation survives reloads (the connect bootstrap lands
  // back on the user's most recent conversation).
  async newConversation(opts = {}) {
    const r = await fetch('/api/openclaw/chat-sessions', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(opts.title ? { title: opts.title } : {}),
    });
    if (!r.ok) throw new Error(`chat-sessions create failed: HTTP ${r.status}`);
    const conv = await r.json();
    if (!conv || typeof conv.key !== 'string' || !conv.key) {
      throw new Error('chat-sessions create returned no key');
    }
    this.sessionKey = conv.key;
    this.conversation = conv;
    return conv;
  }

  // Single sendMessage method — kicks off a chat.send run on the main
  // session and resolves with { runId }. Streaming reply arrives as `chat`
  // events on the EventTarget; subscribers listen for 'stream' (assistant
  // text deltas + done) and 'event' (tool calls / approvals / progress).
  async sendMessage(text, opts = {}) {
    const sessionKey = opts.sessionKey || this.sessionKey || 'agent:main:main';
    const runId = opts.runId || `run-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const payload = await this._send({
      type: 'req',
      id: nextId('chat-send'),
      method: 'chat.send',
      params: {
        sessionKey,
        message: text,
        idempotencyKey: runId,
        ...(opts.thinking !== undefined ? { thinking: opts.thinking } : {}),
      },
    });
    return { runId, payload };
  }

  async chatHistory(opts = {}) {
    const sessionKey = opts.sessionKey || this.sessionKey || 'agent:main:main';
    return this._send({
      type: 'req',
      id: nextId('chat-history'),
      method: 'chat.history',
      params: { sessionKey, limit: opts.limit || 50 },
    });
  }

  async chatAbort(opts = {}) {
    const sessionKey = opts.sessionKey || this.sessionKey || 'agent:main:main';
    return this._send({
      type: 'req',
      id: nextId('chat-abort'),
      method: 'chat.abort',
      params: { sessionKey, runId: opts.runId },
    });
  }

  async sessionsList(opts = {}) {
    return this._send({
      type: 'req',
      id: nextId('sessions-list'),
      method: 'sessions.list',
      params: {
        limit: opts.limit || 30,
        activeMinutes: opts.activeMinutes || 60 * 24 * 30,
        includeDerivedTitles: true,
        includeLastMessage: false,
        ...(opts.agentId ? { agentId: opts.agentId } : {}),
      },
    });
  }

  async sessionsReset(opts = {}) {
    const key = opts.key || this.sessionKey || 'agent:main:main';
    return this._send({
      type: 'req',
      id: nextId('sessions-reset'),
      method: 'sessions.reset',
      params: { key, reason: opts.reason || 'panel-clear' },
    });
  }
}

let singleton = null;
export function getGatewayClient() {
  if (!singleton) singleton = new GatewayClient();
  return singleton;
}
